/**
 * Current SPA path from the location pathname (BrowserRouter).
 * Falls back to legacy hash path when present so mid-migration callers stay safe.
 */
export function getAppPath(
  loc: Pick<Location, "pathname" | "hash"> = window.location,
): string {
  const pathname = loc.pathname || "/";
  if (pathname && pathname !== "/") {
    return pathname;
  }
  const rawHash = loc.hash || "";
  if (rawHash.startsWith("#/")) {
    const hashWithoutPound = rawHash.slice(1);
    const [pathPart] = hashWithoutPound.split("?");
    return pathPart || "/";
  }
  return pathname || "/";
}

export type LandingVertical = "healthcare" | "aviation" | "food_service";

/** Map a landing path to its locked vertical slug. */
export function landingVerticalFromPath(path: string): LandingVertical | null {
  const normalized = (path || "/").split("?")[0] || "/";
  if (normalized === "/aviation") return "aviation";
  if (normalized === "/healthcare" || normalized === "/") return "healthcare";
  if (normalized === "/food-service" || normalized === "/food_service") {
    return "food_service";
  }
  return null;
}

/** Read `?vertical=` from a search string when it is a known landing vertical. */
export function landingVerticalFromSearch(
  search: string,
): LandingVertical | null {
  const raw = new URLSearchParams(search.startsWith("?") ? search : `?${search}`)
    .get("vertical")
    ?.trim()
    .toLowerCase();
  if (raw === "aviation" || raw === "healthcare" || raw === "food_service") {
    return raw;
  }
  return null;
}

/** Left-panel tagline on auth screens (login / signup / forgot password). */
export function authPanelTaglineForVertical(
  vertical: LandingVertical | null,
): string {
  switch (vertical) {
    case "aviation":
      return "Join operators and airports across the U.S. simplifying their aviation compliance.";
    case "food_service":
      return "Join food service teams across the U.S. simplifying their compliance.";
    case "healthcare":
    default:
      return "Join clinics across the U.S. simplifying their healthcare compliance.";
  }
}

/** Home path to return to when closing auth from a vertical-locked entry. */
export function landingHomePathForVertical(
  vertical: LandingVertical | null,
): string {
  switch (vertical) {
    case "aviation":
      return "/aviation";
    case "food_service":
      return "/food-service";
    case "healthcare":
      return "/healthcare";
    default:
      return "/";
  }
}

/** Routes intentionally accessible without authentication. */
export function isPublicPath(path: string): boolean {
  if (path === "/") return true;
  if (path === "/healthcare") return true;
  if (path === "/aviation") return true;
  if (path === "/login") return true;
  if (path === "/signup") return true;
  if (path === "/signup-request") return true;
  if (path === "/campaign-60-day-pilot-A") return true;
  if (path === "/Microsoft-Supported-60-Day-Compliance-Pilot") return true;
  if (path === "/forgot-password") return true;
  if (path === "/verify-otp") return true;
  if (path === "/email-verification") return true;
  if (path === "/reset-password") return true;
  if (path === "/reset-password-success") return true;
  if (path === "/confirm-invitation") return true;
  return false;
}
